import type { RouterContextType } from "@/api/permissions/permissions.api";
import servengineIcon from "@/assets/servengine-icon.svg";
import { Button } from "@/components/ui/Button/Button";
import { useResendServiceProviderEmail } from "@/hooks/service-provider/useServiceProviderMutations";
import {
  useServiceProviderOnboardingStore,
  type OnboardingCompanion,
} from "@/store/useServiceProviderOnboardingStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { Box, Flex, Stack, Text } from "@mantine/core";
import { Card } from "@onengine/component-library";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import styles from "./complete-onboarding.module.scss";
import { handleApiSuccess, showErrorAlert } from "@/utils/responseHandler";

export const Route = createFileRoute(
  "/service-providers/onboarding/_onboardingLayout/complete-onboarding/",
)({
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
  component: CompleteOnboarding,
});

export interface Helper {
  id: string;
  initials: string;
  name: string;
}

function CompleteOnboarding() {
  const navigate = useNavigate();
  const { companions, serviceProviderId, reset, setSelectedCompanion, serviceProvider } =
    useServiceProviderOnboardingStore();
  const { mutate: resendEmail, isPending: isResendingEmail } = useResendServiceProviderEmail();

  const handleBeginOnboarding = (companion: OnboardingCompanion) => {
    reset();
    setSelectedCompanion(companion);
    navigate({
      to: "/service-providers/onboarding/$providerType/$stepId",
      params: { providerType: "independent_tech", stepId: "personal-info" },
    });
  };

  const handleResendEmail = (): void => {
    resendEmail(serviceProviderId ?? "", {
      onSuccess: () => {
        handleApiSuccess("Email sent successfully");
      },
      onError: () => {
        showErrorAlert("Failed to resend email. Please try again.");
      },
    });
  };

  const handleReturnToList = () => {
    navigate({ to: "/service-providers" });
  };

  // No store reset here: the completion page must survive browser Back, and every
  // new onboarding already resets the store at its entry points (the SP list's
  // provider-type picker and handleBeginOnboarding above).
  const goToAlignment = () => {
    const providerType = serviceProvider?.serviceProviderType;

    if (!serviceProviderId || !providerType) {
      showErrorAlert(
        "Missing service provider information. Please return to the SP list and open the profile.",
        "Unable to open Alignment",
      );
      return;
    }

    navigate({
      to: "/service-providers/$id/alignment/$providerType",
      params: { id: serviceProviderId, providerType },
    });
  };

  return (
    <Box className={styles.container}>
      <Stack gap="lg" align="left">
        {/* Logo */}
        <img src={servengineIcon} alt="Servengine" className={styles.logo} />

        {/* Success Heading */}
        <Text className={styles.heading}>Service Provider added successfully!</Text>

        {/* Email Confirmation Card */}
        <Card className={styles.card} shadow="sm" padding="lg" radius="md">
          <Stack gap="md">
            <Text className={styles.cardTitle}>Email confirmation sent to SP</Text>
            <Text className={styles.cardText}>
              A welcome email has been sent to the Service Provider with login details and
              instructions to further complete their profile.
            </Text>
            <Text className={styles.cardText}>
              If they did not receive the email, you can{" "}
              <Text
                component="span"
                className={styles.link}
                onClick={handleResendEmail}
                style={{
                  cursor: isResendingEmail ? "not-allowed" : "pointer",
                  opacity: isResendingEmail ? 0.6 : 1,
                }}
              >
                {isResendingEmail ? "sending..." : "resend it"}
              </Text>
              .
            </Text>
          </Stack>
        </Card>

        {/* Helpers Approved Card - Optional */}
        {companions && companions?.length > 0 && (
          <Card className={styles.card} shadow="sm" padding="lg" radius="md">
            <Stack gap="md">
              <Text className={styles.cardTitle}>Helpers approved for Onboarding</Text>
              <Text className={styles.cardText}>
                The Service Provider has authorized onboarding of their helpers. Select a helper
                below to get started.
              </Text>
              <Stack gap="sm">
                {companions?.map((helper) => (
                  <Flex
                    key={helper?.id}
                    justify="space-between"
                    align="center"
                    className={styles.helperItem}
                  >
                    <Flex align="center" gap="md">
                      <Box className={styles.avatar}>
                        <Text className={styles.avatarText}>
                          {helper?.personalInfoFirstName?.charAt(0) ?? ""}
                          {helper?.personalInfoLastName?.charAt(0) ?? ""}
                        </Text>
                      </Box>
                      <Stack gap={0}>
                        <Text className={styles.helperName}>
                          {[helper.personalInfoFirstName, helper.personalInfoLastName]
                            .filter(Boolean)
                            .join(" ")}
                        </Text>
                        <Text className={styles.phoneNumber}>{helper.personalInfoPhone}</Text>
                      </Stack>
                    </Flex>
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={() => handleBeginOnboarding(helper)}
                    >
                      Begin Onboarding
                    </Button>
                  </Flex>
                ))}
              </Stack>
            </Stack>
          </Card>
        )}

        {/* Return to SP List Button */}
        <Box className={styles.buttonContainer}>
          <Button
            variant="neutral-primary"
            size="base"
            onClick={handleReturnToList}
            className={styles.returnButton}
          >
            Return To SP List
          </Button>
          {serviceProvider?.serviceProviderType && (
            <Button
              variant="neutral-primary"
              size="base"
              onClick={goToAlignment}
              className={styles.returnButton}
            >
              Go To Alignment
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
